import { useCallback, useRef, useState } from 'react';
import { Check, Copy } from 'lucide-react';

export interface PanelCodeBlockProps {
  props: { code: string; language?: string };
}

export function PanelCodeBlock({ props }: PanelCodeBlockProps) {
  const { code, language } = props;
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setCopied(false), 2000);
    });
  }, [code]);

  return (
    <div className="rounded-sd-sm border border-sd-border/30 overflow-hidden my-1">
      {/* Header bar */}
      <div className="flex items-center justify-between bg-sd-surface-elevated px-3 py-1 border-b border-sd-border/30">
        <span className="text-[10px] font-medium uppercase tracking-wider text-sd-text-muted/60 select-none">
          {language ?? 'code'}
        </span>
        <button
          type="button"
          aria-label={copied ? 'Copied' : 'Copy code'}
          onClick={handleCopy}
          className={[
            'flex items-center gap-1 px-1.5 py-0.5 rounded-sd-sm text-sd-xs',
            'transition-[color,background-color] duration-150',
            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-sd-primary/50',
            copied
              ? 'text-sd-success'
              : 'text-sd-text-muted hover:text-sd-warning hover:bg-sd-warning/10',
          ].join(' ')}
        >
          {copied ? <Check size={12} aria-hidden="true" /> : <Copy size={12} aria-hidden="true" />}
          <span>{copied ? 'Copied!' : 'Copy'}</span>
        </button>
      </div>
      {/* Code area */}
      <pre className="bg-sd-background/50 p-3 overflow-x-auto text-sd-sm font-mono text-sd-text leading-relaxed">
        <code>{code}</code>
      </pre>
    </div>
  );
}
